import { createSlice } from '@reduxjs/toolkit';
import { filter, includes, map, uniqBy } from 'lodash';

import socket from '../../utils/socket';

/**
 * Initial state for the orders slice, setting up default values and structures
 * for loading status, error management, order data, and filter settings.
 */
const initialState = {
  isLoading: false,
  error: false,
  orders: [],
  order: {},
  sortBy: [],
  sortOrders: [],
  filterOptions: {
    clients: [],
    years: []
  },
  filters: {
    clients: [],
    years: [],
    unassigned: false,
    inProgress: false,
    finished: false
  }
};

/**
 * Slice for orders that handles state changes related to order operations.
 * This includes loading states, sorting, filtering, and fetching data for orders.
 */
const slice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    // LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
      console.error(state.error);
    },

    getOrders(state, action) {
      const { purchases: orders } = action.payload;
      const clients = uniqBy(orders, (el) => el.clientId).map(extractClients);
      const years = uniqBy(orders, 'year').map(extractYears);
      state.orders = map(orders, (el) => el);
      state.filterOptions.clients = clients;
      state.filterOptions.years = years;
      state.filters.years = years[0] ? [years[0]] : [];
      state.isLoading = false;
    },

    getOrder(state, action) {
      const { purchase: order } = action.payload;
      state.order = order;
      state.isLoading = false;
    },

    updateOrder(state, action) {
      const { purchase: order } = action.payload;
      state.order = order;
      state.isLoading = false;
    },

    // SORT
    sortByOrders(state, action) {
      const idx = state.sortBy.findIndex((el) => el === action.payload);
      if (idx >= 0) {
        if (state.sortOrders[0] === 'desc') {
          state.sortBy.splice(0, 1);
          state.sortOrders.splice(0, 1);
        } else {
          state.sortOrders[0] = 'desc';
        }
      } else {
        state.sortBy[0] = action.payload;
        state.sortOrders[0] = 'asc';
      }
    },

    // FILTER
    ordersFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },

    // RESET FILTER
    resetOrdersFilters(state, action) {
      state.filters = initialState.filters;
      action.payload = initialState.filters;
    }
  }
});

// Exports the reducer function for use in the Redux store
export default slice.reducer;

// Exports the defined Redux actions
export const { setInvoice, sortByOrders, ordersFilters, resetOrdersFilters } = slice.actions;

/**
 * Thunks that handle asynchronous operations related to orders, including
 * fetching order data from a server using socket communications.
 */
export function getOrders() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await socket.get('/purchases');
      dispatch(slice.actions.getOrders(result));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

/**
 * Thunk function to fetch a single order based on its order ID. It behaves similar to getOrders,
 * handling loading states and errors.
 */
export function getOrder(orderId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await socket.get('/purchases', { purchaseOrderId: orderId });
      dispatch(slice.actions.getOrder(result));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function updateOrder(fileId, orderId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await socket.put('/purchases', { fileId, purchaseOrderId: orderId });
      dispatch(slice.actions.getOrder(result));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

/**
 * Filters and applies predefined conditions to the dataset.
 * @param {Array} datas - Array of data to filter.
 * @param {Object} filters - Filters to apply.
 * @returns {Array} The filtered dataset.
 */
export function applyFilter(datas, filters) {
  if (filters.clients.length) {
    const ids = map(filters.clients, (el) => el.id);
    datas = filter(datas, (_data) => includes(ids, _data.client_id));
  }
  if (filters.years.length) {
    const ids = map(filters.years, (el) => el.id);
    datas = filter(datas, (_data) => includes(ids, _data.year));
  }
  return datas;
}

/**
 * Utility functions to extract client and year data from order objects.
 */
function extractClients(invoice) {
  return { id: invoice.clientId, code: invoice.clientCode, name: invoice.clientName };
}

function extractYears(data) {
  return { id: data.year, name: data.year.toString() };
}
